import type { AppDispatch, RootState } from '@/shared/store'
import { storiesSlidesLayersSyncCreate } from '@/shared/api/generated/slide-layers/slide-layers'
import { currentLayerAdapter, originalLayerAdapter } from '../../layer/layerAdapters'
import { slidesAdapter } from '../../slide/slideAdapter'
import type { LocalLayer } from '../../layer/types'
import { slideSyncPending, slideSyncFulfilled, slideSyncRejected } from '../actions'
import { computeLayerOperations } from '../utils/computeOperations'

/**
 * Sync a single slide's layer changes to the server via the batch sync endpoint.
 */
export const syncSlide = (
  payload: { slideId: number },
) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  const { slideId } = payload
  const state = getState()
  const { storyId } = state.editor.sync
  const slide = slidesAdapter.getSelectors().selectById(state.editor.slides, slideId)

  if (!storyId || !slide) {
    throw new Error('Missing storyId or slide state')
  }

  // Get current and original layers for this slide
  const allCurrent = currentLayerAdapter.getSelectors().selectAll(state.editor.layers.current)
  const allOriginal = originalLayerAdapter.getSelectors().selectAll(state.editor.layers.original)
  const currentLayers = allCurrent.filter((l) => l.slideId === slideId)
  const originalLayers = allOriginal.filter((l) => l.slideId === slideId)

  const operations = computeLayerOperations(
    currentLayers,
    originalLayers,
    slide.deletedLayerIds,
  )

  // Nothing to sync
  if (operations.length === 0) return

  dispatch(slideSyncPending())

  try {
    const response = await storiesSlidesLayersSyncCreate(
      storyId,
      slide.slideNumber,
      { operations },
    )

    // Convert response layers to LocalLayer with slideId
    const serverLayers = (response.layers ?? []).map((l) => ({
      ...structuredClone(l),
      slideId,
    })) as LocalLayer[]

    dispatch(slideSyncFulfilled({ slideId, layers: serverLayers }))
  } catch (error) {
    dispatch(slideSyncRejected({
      error: error instanceof Error ? error.message : 'Sync failed',
    }))
    throw error
  }
}
